// CRC32 (IEEE 802.3 polynomial, 0xEDB88320 reflected) — table-driven.
//
// Used by the file-transfer state machine to checksum the assembled byte
// stream; receiver verifies on `file-eof`. This is a transport-integrity
// check, NOT a cryptographic hash — collisions are trivially constructable
// and that's fine for catching transport corruption. Real integrity comes
// from DTLS at the DataChannel layer.
//
// We hand-roll this (no dep) because:
//   - CRC32 is ~30 lines table-driven
//   - we want a streaming API (Crc32Stream) to avoid double-iterating the
//     bytes between "build chunk" and "hash chunk" hot paths
//   - the canonical reflected/no-reflect/post-XOR variants matter; pinning
//     the impl to the file lets us test against canonical vectors

// Standard IEEE 802.3 reflected polynomial table. Precomputed once at
// module load — ~1KB of memory; trivial.
const TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    t[n] = c >>> 0;
  }
  return t;
})();

/**
 * One-shot CRC32 over a byte array.
 *
 * @param {Uint8Array} bytes
 * @param {number} [seed=0]  the previous CRC if you're chaining (already
 *                           post-XORed with 0xffffffff, like our streaming
 *                           class's intermediate state)
 * @returns {number}  unsigned 32-bit
 */
export function crc32(bytes, seed = 0) {
  // Convert post-XOR seed back to the pre-XOR running state. seed=0 (default)
  // produces the canonical CRC for the input bytes alone.
  let crc = (seed ^ 0xffffffff) >>> 0;
  for (let i = 0; i < bytes.length; i++) {
    crc = (TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)) >>> 0;
  }
  return (crc ^ 0xffffffff) >>> 0;
}

/**
 * Convert a 32-bit unsigned integer to an 8-char lowercase hex string.
 * Used for the on-wire `checksum` field in the `file-eof` envelope.
 * @param {number} n
 * @returns {string}
 */
export function crc32ToHex(n) {
  return (n >>> 0).toString(16).padStart(8, '0');
}

/**
 * Streaming CRC32. Update incrementally as chunks arrive; call `.digest()`
 * for the final hex string. Cheaper than reassembling-then-hashing in the
 * receive path.
 */
export class Crc32Stream {
  constructor() {
    // Internal running state is the pre-XOR form (i.e. `crc` before the
    // final ^ 0xffffffff). Starts at all-ones per the standard.
    this._crc = 0xffffffff;
  }

  /**
   * Feed bytes into the running checksum.
   * @param {Uint8Array | ArrayBuffer} chunk
   */
  update(chunk) {
    const bytes = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
    let crc = this._crc;
    for (let i = 0; i < bytes.length; i++) {
      crc = (TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)) >>> 0;
    }
    this._crc = crc;
  }

  /**
   * Finalize and return the CRC32 as an unsigned 32-bit integer.
   * Stream is NOT reset — call `reset()` if you want to reuse the instance.
   * @returns {number}
   */
  value() {
    return (this._crc ^ 0xffffffff) >>> 0;
  }

  /**
   * Finalize and return the CRC32 as 8-char lowercase hex.
   * @returns {string}
   */
  digest() {
    return crc32ToHex(this.value());
  }

  /** Reset to the initial state so the same instance can hash another stream. */
  reset() {
    this._crc = 0xffffffff;
  }
}
